// Third-party imports
import { HttpError } from 'react-admin';

// Local imports - types
import type {
  Repair,
  RepairDetail,
  RepairsDataProvider,
  SubmitReviewArgs,
} from '@src/types';

// Local imports - shared utilities
import { requirePartner } from '../shared';
import { DashboardCurrency } from '@src/utils/valueFormatters';

// Local imports - API
import * as RewareApi from '@src/apis/reware';

/**
 * Maps React Admin field names to GraphQL API sort parameter field names.
 *
 * The GraphQL API has a discrepancy between response field names and sorting parameter field names:
 * - API Response: returns fields named `dateInitiated`, `dateUpdated`, and `status`
 * - API Sorting: expects sortColumn parameter values like `initiatedAt`, `updatedAt`, and `state`
 *
 * React Admin convention is to use the API response field names (e.g., `dateInitiated`)
 * as the `source` prop for table columns. This mapping function bridges that gap by
 * converting React Admin's field names to the API's expected sort parameter values.
 *
 */
const mapSortField = (field: string): string => {
  const sortFieldMap: Record<string, string> = {
    dateInitiated: 'initiatedAt',
    dateUpdated: 'updatedAt',
    status: 'state',
  };
  return sortFieldMap[field] || field;
};

/* There are some "any" assertions in return data to satisfy the generic approach to using the React Admin dataProvider. Without these,
React Admin will try to warn us that a dataProvider method could be called with another resource method. When calling methods explicitly,
use type parameters for type safety:
dataProvider.getList<Repair>(DashboardResources.Repairs, params) returns Promise<GetListResult<Repair>>
*/
export const repairsDataProvider: RepairsDataProvider = {
  getList: async (resource, params) => {
    try {
      const currentPartner = requirePartner();
      const { page = 1, perPage = 25 } = params.pagination || {};
      const { field = 'dateUpdated', order = 'DESC' } = params.sort || {};
      const {
        searchQuery = '',
        state = '',
        initiatedAfter = '',
        initiatedBefore = '',
        repairAttributes = undefined,
      } = params.filter || {};

      const queryParams = {
        partnerCode: currentPartner.code,
        page,
        pageSize: perPage,
        sortColumn: mapSortField(field),
        sortDirection: order,
        searchQuery: searchQuery || undefined,
        state: state || undefined,
        initiatedAfter: initiatedAfter || undefined,
        initiatedBefore: initiatedBefore || undefined,
        repairAttributes,
      };

      // Fetch both repairs list and filter data in parallel
      // Using Promise.allSettled to handle partial failures gracefully
      const [repairsResult, filtersResult] = await Promise.allSettled([
        RewareApi.getRepairs(queryParams),
        RewareApi.getRepairFilters({ partnerCode: currentPartner.code }),
      ]);

      // Check if repairs fetch failed (primary data - must succeed)
      if (repairsResult.status === 'rejected') {
        if (repairsResult.reason instanceof HttpError) throw repairsResult.reason;
        const message = repairsResult.reason instanceof Error ? repairsResult.reason.message : repairsResult.reason;
        // this will get logged in the method's catch block
        throw new HttpError(message, 500);
      }

      const repairsResponse = repairsResult.value;

      // Handle filters fetch failure (supplementary data - graceful degradation)
      let filtersResponse = {};
      if (filtersResult.status === 'rejected') {
        // Log and continue if repairs data has loaded
        console.error('RepairsDataProvider.getList - filters fetch failed:', {
          resource,
          params,
          error: filtersResult.reason instanceof Error ? filtersResult.reason.message : filtersResult.reason,
        });
      } else {
        filtersResponse = filtersResult.value;
      }

      // Handle null/empty response
      if (!repairsResponse || !repairsResponse.entries) {
        return {
          data: [],
          total: 0,
          meta: {
            filters: filtersResponse,
          },
        };
      }

      const repairs: Repair[] = repairsResponse.entries.map(entry => ({
        ...entry,
        id: entry.uuid, // React Admin requires an 'id' field
      }));

      return {
        // eslint-disable-next-line
        data: repairs as any, // see dataProvider comment
        total: repairsResponse.pageCount * perPage || 0,
        meta: {
          filters: filtersResponse,
        },
      };
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw
      // This is additional detail logging
      console.error('RepairsDataProvider.getList failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to load repairs.', status);
    }
  },

  getOne: async (resource, params) => {
    try {
      const currentPartner = requirePartner();

      const response = await RewareApi.getRepairDetail({
        partnerCode: currentPartner.code,
        uuid: params.id as string,
      });

      const transformedRepairItems = response.repairItems?.map(item => ({
        ...item,
        repairItemIssues: item.repairItemIssues?.map(issue => ({
          ...issue,
          price: DashboardCurrency.fromMinorUnit(issue.price),
          initiatedPrice: DashboardCurrency.fromMinorUnit(issue.initiatedPrice),
        })),
      }));

      const repairDetail: RepairDetail = {
        ...response,
        id: response.uuid, // React Admin requires an 'id' field
        price: DashboardCurrency.fromMinorUnit(response.price),
        shippingPrice: DashboardCurrency.fromMinorUnit(response.shippingPrice),
        repairItems: transformedRepairItems,
      };

      // eslint-disable-next-line
      return { data: repairDetail as any }; // see dataProvider comment
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw and it will log automatically
      // This is additional detail logging
      console.error('RepairsDataProvider.getOne failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to load repair details', status);
    }
  },

  getMany: async (resource, params) => {
    // Not needed for current repair functionality
    throw new Error('getMany not implemented');
  },

  getManyReference: async (resource, params) => {
    // Not needed for current repair functionality
    throw new Error('getManyReference not implemented');
  },

  create: async (resource, params) => {
    // Not needed for current repair functionality (repairs are created by customers)
    throw new Error('Create not implemented');
  },

  update: async (resource, params) => {
    try {
      const formData = params.data;
      const previousData = params.previousData as RepairDetail;

      // Transform form data to API format
      // NOTE: 'APPROVED_FREE' is a UI-only state (not in backend database).
      // It's transformed here to 'APPROVED' with all prices set to 0, allowing
      // partners to easily approve repairs as free without manually zeroing prices.
      // Backend only receives: APPROVED, PAYMENT_PENDING, or REJECTED.
      const isFree = formData.repairStatus === 'APPROVED_FREE';
      const isRepairRejected = formData.repairStatus === 'REJECTED';

      // Build issue updates from form data
      const issueUpdates = [];
      if (previousData.repairItems) {
        for (const item of previousData.repairItems) {
          if (item.repairItemIssues) {
            for (const issue of item.repairItemIssues) {
              const issueState = isRepairRejected ?
                'REJECTED' :
                formData.issueStates?.[issue.uuid];

              const issuePrice = isFree ?
                0 :
                (formData.issuePrices?.[issue.uuid] !== undefined ?
                  Number(formData.issuePrices[issue.uuid]) :
                  (issue.price || 0));

              issueUpdates.push({
                uuid: issue.uuid,
                state: issueState === 'REJECTED' ? 'REJECTED' : (isFree ? 'APPROVED' : 'PAYMENT_PENDING'),
                rejectionReason: formData.issueRejectionReasons?.[issue.uuid] || null,
                price: DashboardCurrency.toMinorUnit(issuePrice),
              });
            }
          }
        }
      }

      const repairState = isFree ?
        'APPROVED' :
        (isRepairRejected ? 'REJECTED' : 'PAYMENT_PENDING');

      const shippingPrice = isFree ?
        0 :
        (formData.shippingPrice || 0);

      const reviewParams: SubmitReviewArgs = {
        uuid: formData.id,
        repairState,
        repairRejectionReason: formData.repairRejectionReason || null,
        repairShippingPrice: DashboardCurrency.toMinorUnit(shippingPrice),
        issueUpdates,
      };

      // possible TODO: update reware-api to return repair UUID after review
      // currently this is returning null for a successful submission
      await RewareApi.submitReview(reviewParams);

      // Optimistic update on formData.id since submitReview returns null
      // eslint-disable-next-line
      return { data: { id: formData.id } as any }; // see dataProvider comment
    } catch (error) {
      // React Admin requires we not return undefined so we will re-throw and it will log automatically
      // This is additional detail logging
      console.error('RepairsDataProvider.update failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      // React Admin will also log/notify the user what is thrown here
      // so we generalize this message
      const status = error.status ? error.status : 500;
      throw new HttpError('Failed to update repair', status);
    }
  },

  updateMany: async (resource, params) => {
    // Not needed for current repair functionality
    throw new Error('UpdateMany not implemented');
  },

  delete: async (resource, params) => {
    // Not needed for current repair functionality
    throw new Error('Delete not implemented');
  },

  deleteMany: async (resource, params) => {
    // Not needed for current repair functionality
    throw new Error('DeleteMany not implemented');
  },

  cancel: async (_resource, params) => {
    try {
      const { repairUuid } = params;

      const repair = await RewareApi.cancelRepair({
        repairUuid,
      });

      return repair;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to cancel repair.', 500);
    }
  },

  refund: async (_resource, params) => {
    try {
      const { repairUuid } = params;

      const repair = await RewareApi.refundRepair({
        repairUuid,
      });

      return repair;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to refund repair.', 500);
    }
  }
};

export default repairsDataProvider;
